// The editing rule for settings pages (D221), as one small reusable piece. Mock only.
//
// - Owners and admins edit; everyone else sees the same values read-only, as plain text.
//   The role comes from the existing mock switch the Settings shell already uses: `?role=viewer`
//   (`?role=staff` and `?role=readonly` mean the same). Default is the practice owner.
// - Every editable section ends with "Changed by {name} ({role}) {date}." That line updates when
//   a value changes. Today's mock date for these pages is Oct 22.
// - Changes save as they are made (no Save button): number fields commit on blur or Enter and
//   flash a brief "Saved". A change applies from that day on.
// - Each change is also kept in a small in-memory list (getSettingsChanges). ELVA's work log has
//   no simple hook for settings changes, so nothing is written to it yet.
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

// ── who and when ──

/** The signed-in mock user: the practice owner. */
export const SETTINGS_USER = { name: 'Dr. Emily Carter', role: 'practice owner' } as const;
/** Today in the mock, for "Changed by …" lines on these pages. */
export const SETTINGS_TODAY = 'Oct 22';

/** Mock only: `?role=viewer` (or staff / readonly) makes every settings page read-only. */
export function canEditSettings(): boolean {
  try {
    const r = new URLSearchParams(window.location.search).get('role')?.toLowerCase();
    return !(r === 'viewer' || r === 'staff' || r === 'readonly');
  } catch {
    return true;
  }
}

/** Owners and admins can edit; everyone else reads. Read once per mount. */
export function useCanEditSettings(): boolean {
  const [can] = useState(canEditSettings);
  return can;
}

// ── the change record ──

export interface SettingsChange {
  /** Which section changed, e.g. 'follow-up-flags'. */
  id: string;
  what: string;
  by: string;
  role: string;
  date: string;
}

let changes: SettingsChange[] = [];
let version = 0;
const listeners = new Set<() => void>();
function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
function bump(): void {
  version++;
  for (const l of listeners) l();
}

/** Record a settings change: updates that section's footer. */
export function recordSettingsChange(id: string, what: string): void {
  changes = [{ id, what, by: SETTINGS_USER.name, role: SETTINGS_USER.role, date: SETTINGS_TODAY }, ...changes];
  bump();
}
export function getSettingsChanges(): readonly SettingsChange[] {
  return changes;
}
export function useSettingsVersion(): number {
  return useSyncExternalStore(subscribe, () => version, () => version);
}

/**
 * The footer under each editable section: "Changed by Dr. Emily Carter (practice owner) Oct 1."
 * `initial` is the date of the last change before this session (e.g. 'Oct 1'); leave it out when
 * nothing has been changed yet and the section is still on ELVA's defaults.
 */
export function SettingsChangeFooter({
  id,
  initial,
  note,
  className,
}: {
  id: string;
  initial?: string;
  /** Extra sentence after the line, e.g. "Applies to claims already waiting, too." */
  note?: string;
  className?: string;
}) {
  useSettingsVersion();
  const canEdit = useCanEditSettings();
  const last = changes.find((c) => c.id === id);
  const line = last
    ? `Changed by ${last.by} (${last.role}) ${last.date}.`
    : initial
      ? `Changed by ${SETTINGS_USER.name} (${SETTINGS_USER.role}) ${initial}.`
      : "Not changed yet: these are ELVA's defaults.";
  return (
    <div className={className ?? 'setd-change'}>
      {canEdit ? null : 'Only owners and admins can change these. '}
      {line}
      {note ? ` ${note}` : ''}
    </div>
  );
}

// ── "Saved" hint ──

/** A brief 'Saved' that shows for about two seconds after `flash()`. */
export function useSavedHint(): { saved: boolean; flash: () => void } {
  const [saved, setSaved] = useState(false);
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (t.current) clearTimeout(t.current);
    },
    [],
  );
  const flash = () => {
    setSaved(true);
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => {
      setSaved(false);
    }, 2000);
  };
  return { saved, flash };
}

export function SavedHint({ show }: { show: boolean }) {
  return (
    <span className="setd-ed-saved" role="status" aria-live="polite">
      {show ? 'Saved' : ''}
    </span>
  );
}

// ── the number field ──

/**
 * A whole-number setting. Editable for owners and admins: saves on blur or Enter, clamps to
 * min–max with an inline message, flashes "Saved". Read-only: plain text, no input.
 */
export function NumberSetting({
  label,
  value,
  min,
  max,
  onCommit,
  readOnly,
  prefix,
  suffix,
  unit,
  width,
}: {
  /** Accessible name, e.g. "Minutes for a payment posted". */
  label: string;
  value: number;
  min: number;
  max: number;
  /** Called with the new, valid, clamped value, only when it differs from `value`. */
  onCommit: (next: number) => void;
  readOnly?: boolean;
  /** Shown before the number, e.g. "$". */
  prefix?: string;
  /** Shown after the number, e.g. " min". */
  suffix?: string;
  /** Used in the range message, e.g. "minutes". */
  unit?: string;
  width?: number;
}) {
  const [draft, setDraft] = useState(String(value));
  const [msg, setMsg] = useState('');
  const { saved, flash } = useSavedHint();
  const range = `${prefix ?? ''}${String(min)} to ${prefix ?? ''}${String(max)}${unit ? ` ${unit}` : ''}`;
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  if (readOnly) {
    return (
      <span className="mono">
        {prefix}
        {value}
        {suffix}
      </span>
    );
  }

  const commit = () => {
    const trimmed = draft.trim();
    if (!/^\d+$/.test(trimmed)) {
      setMsg(`Enter a whole number from ${range}.`);
      setDraft(String(value));
      return;
    }
    const n = Number(trimmed);
    const clamped = Math.max(min, Math.min(max, n));
    setMsg(clamped === n ? '' : `Kept within ${range}: set to ${prefix ?? ''}${String(clamped)}.`);
    setDraft(String(clamped));
    if (clamped !== value) {
      onCommit(clamped);
      flash();
    }
  };

  const invalidId = `setd-ed-msg-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <span className="setd-ed-wrap">
      <span className="setd-ed-num">
        {prefix ? <span aria-hidden>{prefix}</span> : null}
        <input
          className="setd-input mono setd-ed-input"
          style={width ? { width } : undefined}
          inputMode="numeric"
          aria-label={label}
          aria-invalid={msg ? true : undefined}
          aria-describedby={msg ? invalidId : undefined}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setMsg('');
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setDraft(String(value));
              setMsg('');
            }
          }}
        />
        {suffix ? <span aria-hidden>{suffix}</span> : null}
        <SavedHint show={saved && !msg} />
      </span>
      {msg ? (
        <span id={invalidId} className="setd-ed-msg" role="alert">
          {msg}
        </span>
      ) : null}
    </span>
  );
}
